import {isFeatureEnabled} from '@github-ui/feature-flags'
import {referenceID} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {sendEvent} from '@github-ui/hydro-analytics'
import {TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, ButtonGroup} from '@primer/react'
import {useCallback, useState} from 'react'
import {NewAgentTaskButton} from '@github-ui/new-agent-task-button/NewAgentTaskButton'
import type {Repository} from '@github-ui/current-repository'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {getExplainSnippetPrompt, getSuggestImprovementsPrompt} from '@github-ui/copilot-chat/utils/prompts'

import {publishAddCopilotChatReference, publishOpenCopilotChat} from '@github-ui/copilot-chat/utils/copilot-chat-events'
import {
  CopilotChatIntents,
  type CopilotChatReference,
  type CopilotChatEventPayload,
} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {AskCopilotButton} from '../AskCopilotButton'
import sharedStyles from '../shared/CopilotSplitButton.module.css'
import type {CreateTaskReference} from '@github-ui/agent-sessions/components/new-task-chat-input/NewTaskChatInput'

const DROPDOWN_BUTTON_ID = 'code-line-dropdown-copilot-button'

type CopilotCodeLinesMenuProps = {
  copilotAccessAllowed: boolean
  messageReference: CopilotChatReference
  hideDropdown?: boolean
  id?: string
  showAgentTaskButton?: boolean
  currentRepo?: Repository
  currentReference?: CreateTaskReference
}

export default function CopilotCodeLinesMenu({
  copilotAccessAllowed,
  messageReference,
  hideDropdown,
  id,
  showAgentTaskButton,
  currentRepo,
  currentReference,
}: CopilotCodeLinesMenuProps) {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<'actionMenu' | 'newTask'>('actionMenu')
  const openingThreadSwitchEnable = isFeatureEnabled('copilot_chat_opening_thread_switch')

  const handleExplain = () => {
    sendChatEvent(messageReference.type, getChatMode(messageReference.type))
    if (copilotFeatureFlags.copilotRedirectSnippetToImmersive && messageReference.type === 'snippet') {
      copilotLocalStorage.setEntrypointMessage({message: getExplainSnippetPrompt(), references: [messageReference]})
      window.location.href = '/copilot'
    } else {
      publishOpenCopilotChat({
        content: 'Explain',
        intent: CopilotChatIntents.explain,
        references: [messageReference],
        id: DROPDOWN_BUTTON_ID,
      })
    }
    setOpen(false)
  }

  const handleSuggest = () => {
    const suggestMessage = getSuggestImprovementsPrompt()
    if (copilotFeatureFlags.copilotRedirectSnippetToImmersive && messageReference.type === 'snippet') {
      sendChatEvent(messageReference.type, getChatMode(messageReference.type))
      copilotLocalStorage.setEntrypointMessage({message: suggestMessage, references: [messageReference]})
      window.location.href = '/copilot'
    } else {
      publishOpenCopilotChat({
        content: suggestMessage,
        intent: CopilotChatIntents.suggest,
        references: [messageReference],
        id: DROPDOWN_BUTTON_ID,
      })
    }
    setOpen(false)
  }

  const handleNewTask = () => {
    setView('newTask')
  }

  const handleAskAbout = useCallback(() => {
    sendChatEvent(messageReference.type, getChatMode(messageReference.type))
    const payload: CopilotChatEventPayload = {
      intent: CopilotChatIntents.conversation,
      references: [messageReference],
      id,
    }

    publishOpenCopilotChat(payload)
    setOpen(false)
  }, [id, messageReference])

  const handleAddReferenceCallback = () => {
    if (openingThreadSwitchEnable) {
      handleAttachReference(messageReference, DROPDOWN_BUTTON_ID)
    } else {
      handleAddReference(messageReference, true, DROPDOWN_BUTTON_ID)
    }
    setOpen(false)
  }

  const handleAskCopilotClick = () => {
    sendChatEvent(messageReference.type, getChatMode(messageReference.type))
    if (messageReference.type === 'file' && copilotFeatureFlags.copilotRedirectFileViewToImmersive) {
      window.location.href = `/copilot?reference_id=${referenceID(messageReference)}`
    } else if (messageReference.type === 'snippet' && copilotFeatureFlags.copilotRedirectSnippetToImmersive) {
      copilotLocalStorage.setCurrentReferences(null, [messageReference])
      window.location.href = '/copilot'
    } else if (hideDropdown) {
      handleAddReference(messageReference, true, id)
    } else {
      handleAskAbout()
    }
  }

  const copilotButtonGroupProps = {
    referenceType: messageReference.type,
    onClick: handleAskCopilotClick,
    id,
  }

  return copilotAccessAllowed ? (
    hideDropdown ? (
      <AskCopilotButton referenceType={messageReference.type} onClick={handleAskCopilotClick} id={id} />
    ) : (
      <>
        {view === 'actionMenu' && (
          <ButtonGroup>
            <AskCopilotButton {...copilotButtonGroupProps} />
            <ActionMenu open={open} onOpenChange={setOpen}>
              <ActionMenu.Button
                // This is all hacky and I wish we could use ActionMenu.Anchor
                // But ActionMenu.Anchor doesn't accept an ID parameter
                // TODO: revert back to how this was before once https://github.com/primer/react/issues/4299 is fixed
                // the empty child fragment is load bearing for button group styles and `ActionMenu.Button` requires a child element
                id={DROPDOWN_BUTTON_ID}
                trailingAction={TriangleDownIcon}
                size="small"
                aria-label="Copilot menu"
                className={sharedStyles.menuButton}
              >
                <></>
              </ActionMenu.Button>
              <ActionMenu.Overlay
                align="end"
                onKeyDown={e => {
                  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                  if (e.key === 'Escape') {
                    e?.stopPropagation()
                    setOpen(false)
                  }
                }}
              >
                <ActionList>
                  <ActionList.Item onSelect={handleExplain}>Explain</ActionList.Item>
                  <ActionList.Item onSelect={handleSuggest}>Suggest improvements</ActionList.Item>
                  {showAgentTaskButton && <ActionList.Item onSelect={handleNewTask}>New agent session</ActionList.Item>}
                  {!copilotFeatureFlags.copilotRedirectSnippetToImmersive && (
                    <>
                      <ActionList.Divider />
                      <ActionList.Item onSelect={handleAddReferenceCallback}>Attach to current thread</ActionList.Item>
                    </>
                  )}
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </ButtonGroup>
        )}
        {view === 'newTask' && (
          <ButtonGroup>
            <AskCopilotButton {...copilotButtonGroupProps} />
            <NewAgentTaskButton
              isIconButton
              icon={TriangleDownIcon}
              size="small"
              label="Copilot menu"
              eventType="new_agent_task_button_codeview_line_selection"
              currentReference={currentReference}
              currentRepo={currentRepo}
              hideRepoSelector
              open
              onClose={() => setView('actionMenu')}
              setContentOpen={(newOpen: boolean) => {
                if (!newOpen) {
                  setView('actionMenu')
                }
              }}
            />
          </ButtonGroup>
        )}
      </>
    )
  ) : null
}

export const handleAttachReference = (messageReference: CopilotChatReference, id?: string) => {
  publishAddCopilotChatReference(messageReference, false, id)
  publishOpenCopilotChat({intent: CopilotChatIntents.conversation, id, attachThread: true})
}

export const handleAddReference = (messageReference: CopilotChatReference, shouldAppend?: boolean, id?: string) => {
  if (shouldAppend) {
    publishAddCopilotChatReference(messageReference, true, id)
    publishOpenCopilotChat({intent: CopilotChatIntents.conversation, id})
  } else {
    publishOpenCopilotChat({intent: CopilotChatIntents.conversation, references: [messageReference], id})
  }
}

function getChatMode(referenceType: string): 'immersive' | 'assistive' {
  if (referenceType === 'file' && copilotFeatureFlags.copilotRedirectFileViewToImmersive) {
    return 'immersive'
  }
  if (referenceType === 'snippet' && copilotFeatureFlags.copilotRedirectSnippetToImmersive) {
    return 'immersive'
  }
  return 'assistive'
}

function sendChatEvent(referenceType: string, mode: 'immersive' | 'assistive') {
  sendEvent('dotcom_chat.activate', {
    target: referenceType,
    mode,
  })
}
